import api from '@/utils/api'

/**
 * 语音电话服务
 * 集成短信云语音接口
 */
class VoiceCallService {
  constructor() {
    this.config = null
  }

  /**
   * 获取语音电话配置
   */
  async getConfig() {
    try {
      const response = await api.get('/voice-call/config')
      if (response.success) {
        this.config = response.data
        return this.config
      }
      return null
    } catch (error) {
      console.error('获取语音电话配置失败:', error)
      return null
    }
  }

  /**
   * 保存语音电话配置
   * @param {Object} config 配置对象
   */
  async saveConfig(config) {
    try {
      const response = await api.post('/voice-call/config', config)
      if (response.success) {
        this.config = config
        return true
      }
      return false
    } catch (error) {
      console.error('保存语音电话配置失败:', error)
      return false
    }
  }

  /**
   * 发送语音电话
   * @param {Object} alarmData 告警数据
   * @param {Array} phones 电话号码列表
   * @param {Object} options 可选参数
   */
  async sendVoiceCall(alarmData, phones = null, options = {}) {
    if (!this.config && !(await this.getConfig())) {
      throw new Error('语音电话配置未初始化')
    }

    if (!this.config.enabled) {
      console.log('语音电话功能已禁用')
      return { success: true, message: '语音电话功能已禁用' }
    }

    // 使用传入的电话号码或默认配置的号码
    const targetPhones = phones || this.config.defaultPhones || []
    
    if (targetPhones.length === 0) {
      throw new Error('没有配置通知电话号码')
    }

    // 构建语音内容
    const contents = this.buildVoiceContents(alarmData)
    
    // 构建请求参数
    const payload = {
      contents: contents,
      phones: targetPhones.filter(phone => phone && phone.trim()),
      state: this.config.state || 1,
      templateId: this.config.templateId || "10102",
      templateId_bak: "",
      time: Date.now(),
      type: this.config.type || 1
    }

    // 构建请求头
    const headers = {
      'appId': this.config.appId,
      'userAccount': this.config.userAccount,
      'authToken': this.config.authToken,
      'Content-Type': 'application/json'
    }

    try {
      // 发送请求到后端，由后端转发到短信云接口
      const response = await api.post('/voice-call/send', {
        payload,
        headers,
        apiUrl: this.config.apiUrl
      })

      if (response.success) {
        console.log('语音电话发送成功:', response.data)
        return response
      } else {
        throw new Error(response.message || '语音电话发送失败')
      }
    } catch (error) {
      console.error('语音电话发送失败:', error)
      throw error
    }
  }

  /**
   * 发送测试语音电话
   * @param {string} phone 测试电话号码
   * @param {string} content 测试内容
   */
  async sendTestCall(phone, content) {
    if (!this.config && !(await this.getConfig())) {
      throw new Error('语音电话配置未初始化')
    }

    const testAlarmData = {
      title: '测试告警',
      content: content || '这是一条测试告警消息',
      device: {
        deviceName: '测试设备',
        locationName: '测试位置'
      },
      createdAt: new Date().toISOString()
    }

    return await this.sendVoiceCall(testAlarmData, [phone])
  }

  /**
   * 构建语音内容
   * @param {Object} alarmData 告警数据
   * @returns {Array} 语音内容数组
   */
  buildVoiceContents(alarmData) {
    const contents = []
    
    // 位置信息
    const location = alarmData.locationName || 
                    alarmData.device?.locationName || 
                    alarmData.locationInfo || 
                    '未知位置'
    contents.push(location)
    
    // 设备信息
    const deviceName = alarmData.device?.deviceName || '未知设备'
    contents.push(deviceName)
    
    // 设备类型
    const deviceType = alarmData.device?.deviceTypeName || '安全设备'
    contents.push(deviceType)
    
    // 告警时间和内容
    const time = this.formatAlarmTime(alarmData.createdAt)
    const alarmTitle = alarmData.title || '安全告警'
    contents.push(`${time}发生${alarmTitle}`)
    
    return contents
  }

  /**
   * 格式化告警时间
   * @param {string} dateTime 时间字符串
   * @returns {string} 格式化后的时间
   */
  formatAlarmTime(dateTime) {
    if (!dateTime) return '刚刚'
    
    const date = new Date(dateTime)
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    
    return `${hours}:${minutes}`
  }

  /**
   * 检查是否应该触发语音电话
   * @param {Object} alarmData 告警数据
   * @returns {boolean} 是否应该触发
   */
  shouldTriggerCall(alarmData) {
    if (!this.config || !this.config.enabled) {
      return false
    }

    // 检查告警级别是否在触发级别中
    const alarmLevel = alarmData.alarmLevelId?.toString()
    if (this.config.triggerLevels && this.config.triggerLevels.length > 0) {
      return this.config.triggerLevels.includes(alarmLevel)
    }

    // 默认对所有告警触发
    return true
  }

  /**
   * 延迟发送语音电话
   * @param {Object} alarmData 告警数据
   * @param {Array} phones 电话号码列表
   */
  async sendDelayedCall(alarmData, phones = null) {
    const delay = (this.config?.callDelay || 0) * 1000 // 转换为毫秒
    
    if (delay > 0) {
      console.log(`延迟 ${delay / 1000} 秒后发送语音电话`)
      setTimeout(() => {
        this.sendVoiceCallWithRetry(alarmData, phones)
      }, delay)
    } else {
      await this.sendVoiceCallWithRetry(alarmData, phones)
    }
  }

  /**
   * 带重试的语音电话发送
   * @param {Object} alarmData 告警数据
   * @param {Array} phones 电话号码列表
   */
  async sendVoiceCallWithRetry(alarmData, phones = null) {
    const maxRetries = this.config?.retryCount || 0
    let attempt = 0

    while (attempt <= maxRetries) {
      try {
        const result = await this.sendVoiceCall(alarmData, phones)
        console.log(`语音电话发送成功 (尝试 ${attempt + 1}/${maxRetries + 1})`)
        return result
      } catch (error) {
        attempt++
        console.error(`语音电话发送失败 (尝试 ${attempt}/${maxRetries + 1}):`, error.message)
        
        if (attempt <= maxRetries) {
          // 重试间隔（递增）
          const retryDelay = Math.min(1000 * Math.pow(2, attempt - 1), 30000)
          console.log(`${retryDelay / 1000} 秒后重试...`)
          await new Promise(resolve => setTimeout(resolve, retryDelay))
        } else {
          console.error('语音电话发送最终失败，已达到最大重试次数')
          throw error
        }
      }
    }
  }

  /**
   * 处理告警触发语音电话
   * @param {Object} alarmData 告警数据
   */
  async handleAlarmTrigger(alarmData) {
    try {
      // 确保配置已加载
      if (!this.config) {
        await this.getConfig()
      }

      // 检查是否应该触发语音电话
      if (!this.shouldTriggerCall(alarmData)) {
        console.log('告警不满足语音电话触发条件')
        return
      }

      console.log('告警触发语音电话:', alarmData.title)
      
      // 发送延迟语音电话
      await this.sendDelayedCall(alarmData)
      
    } catch (error) {
      console.error('处理告警触发语音电话失败:', error)
      // 这里可以添加错误通知或日志记录
    }
  }
}

// 创建单例实例
const voiceCallService = new VoiceCallService()

export default voiceCallService